import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { gameDetailsActions } from '../../store/gameDetailsSlice';
import { userDetailsActions } from '../../store/userDetailsSlice';
import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useGetUserByIdQuery, useDeleteUserMutation, usePromoteUserMutation } from '../../store/services/userServices';
import { useGetRolesQuery } from '../../store/services/rolesServices';
import { openModalWithTimeout } from '../../store/alertSlice';
import { getRoleLabel, isAdmin, ROLES } from '../../utils/roleUtils';
import DeleteConfirm from '../DeleteConfirm';
import FormSelect from '../FormSelect';
import { isActiveUser, registryStatus } from './UserTable';
import { LuUser, LuCalendarRange, LuMail, LuMapPin, LuPhone, LuContact2, LuContact, LuCreditCard } from "react-icons/lu";
import { CircularProgress } from '@mui/material';

const REGISTRY_TEXT = {
    verified: { label: 'Verifikuar', className: 'text-emerald-600' },
    failed: { label: 'Nuk u verifikua', className: 'text-red-600' },
    pending: { label: 'Në pritje të verifikimit', className: 'text-amber-600' },
};

const getCustomStyles = (opacity = 0.8) => ({
    content: {
        position: 'static',
        borderRadius: '10px',
        padding: 0,
        maxWidth: 'calc(100vw - 2rem)',
    },
    overlay: {
        background: `rgba(0, 0, 0, ${opacity})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',          // Blur effect
        WebkitBackdropFilter: 'blur(2px)',   // Safari support
    }
});

Modal.setAppElement(document.getElementById('root'))

export default function UserDetails({ opacity = 0.8 }) {

    const user = useSelector(state => state.user_details.data)
    const isOpen = useSelector(state => state.user_details.isOpen)
    const dispatch = useDispatch()

    const { data: userData, isLoading, isError, isSuccess } = useGetUserByIdQuery(user.id || '', {
        skip: !user.id
    })

    const data = userData?.data || user;

    const closeModal = () => {
        dispatch(userDetailsActions.closeModal())
    }

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
    const [newRoleId, setNewRoleId] = useState('')
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation()
    const [promoteUser, { isLoading: isPromoting }] = usePromoteUserMutation()
    const { data: rolesData } = useGetRolesQuery()

    useEffect(() => setNewRoleId(''), [data?.id])

    const isActive = isActiveUser(data)
    // Only ATK staff accounts have an activation status (set-password link used or not)
    const isInternalUser = Object.values(ROLES).includes(data?.role)
    let currentUser = null
    try {
        currentUser = jwtDecode(localStorage.getItem('giz_superaccess'))
    } catch {
        // no/invalid token — the dashboard wouldn't render without one
    }
    // Admins manage other accounts, never their own
    const canManage = isAdmin() && data?.id && data.id !== currentUser?.id

    /* Role ids rank top-down (1 = super_admin ...). The backend only promotes: the new role must be
       below the admin's own and above the user's current one. Citizens are never promoted to staff. */
    const roles = rolesData?.data || []
    const myRoleId = roles.find((r) => r.name === currentUser?.role)?.id
    const userRoleId = roles.find((r) => r.name === data?.role)?.id
    const promotableRoles = isInternalUser && myRoleId && userRoleId
        ? roles.filter((r) => r.id > myRoleId && r.id < userRoleId)
        : []
    const roleOptions = promotableRoles.map((role) => ({ value: String(role.id), label: getRoleLabel(role.name) }))

    const notify = (text, type) => dispatch(openModalWithTimeout({ text, type }, 3000))
    const errorText = (error, fallback) => error?.data?.error || error?.data?.message || fallback

    const handleConfirmDelete = async () => {
        setIsDeleteConfirmOpen(false)
        try {
            await deleteUser(data.id).unwrap()
            notify('Përdoruesi u fshi me sukses!', 'success')
            closeModal()
        } catch (error) {
            notify(errorText(error, 'Fshirja e përdoruesit dështoi!'), 'error')
        }
    }

    const handlePromote = async () => {
        if (!newRoleId) return
        try {
            await promoteUser({ id: data.id, role_id: Number(newRoleId) }).unwrap()
            notify('Roli u ndryshua me sukses!', 'success')
            setNewRoleId('')
        } catch (error) {
            notify(errorText(error, 'Ndryshimi i rolit dështoi!'), 'error')
        }
    }

    return (
        <div>
            <DeleteConfirm
                isOpen={isDeleteConfirmOpen}
                closeModal={() => setIsDeleteConfirmOpen(false)}
                handleConfirm={handleConfirmDelete}
                title='Fshi përdoruesin?'
                description={`Llogaria e ${data?.first_name || ''} ${data?.last_name || ''} do të fshihet dhe nuk do të mund të kyçet më. Ky veprim nuk mund të kthehet.`}
                confirmLabel='Fshi'
            />
            <Modal
                isOpen={isOpen}
                onRequestClose={closeModal}
                style={getCustomStyles(opacity)}
                contentLabel="Example Modal"
            >
                <div className="bg-white p-5 w-[360px] max-w-full max-h-[90dvh] overflow-y-auto text-sm">
                    {isLoading ? <div className="flex justify-center items-center h-[480px] max-h-[80dvh]"><CircularProgress /></div> : (
                        <div className='flex flex-col gap-4 items-center'>
                            <div className='flex flex-col gap-2 items-center'>
                                <div className='w-14 h-14 rounded-full flex items-center justify-center border border-[#bbb]'>
                                    <LuUser color='#bbb' size={24} />
                                </div>
                                <div className='text-lg sm:text-xl text-center break-words'>{data?.first_name} {data?.last_name}</div>
                            </div>
                            <hr className='w-full' />
                            <div className='flex flex-col gap-2 w-full text-sm'>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>ID</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.id}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Numri personal</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.personal_number}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Numri i telefonit</div>
                                    {/* <div><LuPhone color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.phone_number}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Email</div>
                                    {/* <div><LuMail color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.email}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Ditëlindja</div>
                                    {/* <div><LuCalendarRange color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{new Date(data?.birthdate).toLocaleDateString("en-GB")}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Komuna</div>
                                    {/* <div><LuMapPin color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.municipality_name}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Banka</div>
                                    {/* <div><LuCreditCard color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.bank_name}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Numri i bankës</div>
                                    {/* <div><LuCreditCard color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.bank_account_number}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Data e regjistrimit</div>
                                    {/* <div><LuCreditCard color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{new Date(data?.created_at).toLocaleDateString('en-GB')}</div>
                                </div>
                            </div>
                            <hr className='w-full' />
                            <div className='flex flex-col gap-2 w-full text-sm'>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Pjesëmarrje në lojë</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.participation_count}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Kupona të dorëzuar</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.receipt_count}</div>
                                </div>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Fitore gjithsej</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{data?.winner_count}</div>
                                </div>
                            </div>
                            <hr className='w-full' />
                            <div className='flex flex-col gap-2 w-full text-sm'>
                                <div className='flex justify-between items-center gap-4'>
                                    {/* <div><LuContact color='var(--secondary-color)' size={24} /></div> */}
                                    <div className='text-xs uppercase text-gray-500 min-w-0'>Roli</div>
                                    <div className='text-xs font-medium text-right break-words min-w-0'>{getRoleLabel(data?.role)}</div>
                                </div>
                                {!isInternalUser && (
                                    <div className='flex justify-between items-center gap-4'>
                                        <div className='text-xs uppercase text-gray-500 min-w-0'>Regjistri civil</div>
                                        <div className={`text-xs font-semibold text-right break-words min-w-0 ${REGISTRY_TEXT[registryStatus(data)].className}`}>
                                            {REGISTRY_TEXT[registryStatus(data)].label}
                                        </div>
                                    </div>
                                )}
                                {isInternalUser && (
                                    <div className='flex justify-between items-center gap-4'>
                                        <div className='text-xs uppercase text-gray-500 min-w-0'>Statusi</div>
                                        <div className={`text-xs font-semibold text-right break-words min-w-0 ${isActive ? 'text-emerald-600' : 'text-amber-600'}`}>
                                            {isActive ? 'Aktiv' : 'Në pritje të fjalëkalimit'}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {canManage && (
                                <div className='flex flex-col gap-2 w-full'>
                                    {promotableRoles.length > 0 && (
                                        <div className='flex gap-2'>
                                            <div className='min-w-0 flex-1'>
                                                <FormSelect
                                                    aria-label='Roli i ri'
                                                    placeholder='Ngrit në rolin...'
                                                    isSearchable={false}
                                                    isClearable
                                                    menuPlacement='auto'
                                                    options={roleOptions}
                                                    value={roleOptions.find((o) => o.value === newRoleId) || null}
                                                    onChange={(selected) => setNewRoleId(selected?.value || '')}
                                                />
                                            </div>
                                            <button
                                                type='button'
                                                disabled={!newRoleId || isPromoting}
                                                onClick={handlePromote}
                                                className='shrink-0 rounded-xl bg-secondary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-secondary/90 disabled:opacity-60 disabled:cursor-not-allowed'
                                            >
                                                {isPromoting ? 'Duke ruajtur...' : 'Ruaj'}
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        type='button'
                                        disabled={isDeleting}
                                        onClick={() => setIsDeleteConfirmOpen(true)}
                                        className='w-full rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed'
                                    >
                                        {isDeleting ? 'Duke fshirë...' : 'Fshi përdoruesin'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </div>)
}